import React, { useEffect, useState } from 'react';
import { RouteComponentProps, withRouter, StaticContext } from 'react-router';
import { axiosKK } from '../../services/networkRequest';
import { Customer, CustomerListResponse } from '../../models/APIModels';
import SubmitButton from '../../components/SubmitButton';
import Button from '../../components/Button';
import {
  AdminPageShell,
  DataTable,
  DataTableColumn,
} from '../../components/admin/Shared';

interface CustomersComponentState {
  customers?: Customer[];
}

function AdminCustomersListComponent({
  history,
}: RouteComponentProps<
  undefined,
  StaticContext,
  CustomersComponentState
>): JSX.Element {
  const [customers, setCustomers] = useState<Customer[]>(
    history.location.state?.customers || [],
  );
  const [page, setPage] = useState(0);
  const [hideButton, setHideButton] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    axiosKK
      .get<CustomerListResponse>(`/customers/${page}`)
      .then(({ data }) => {
        if (cancelled) return;
        const next = data.customers;
        if (next.length < 20) setHideButton(true);
        setCustomers((prevState) => [...prevState, ...next]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [page]);

  const columns: DataTableColumn<Customer>[] = [
    {
      key: 'id',
      label: 'ID',
      width: '80px',
      render: (row) => (
        <Button
          variant="link"
          size="sm"
          className="admin-row-link"
          onClick={() => history.push(`/admin/customer/${row.id}`)}
        >
          {row.id}
        </Button>
      ),
    },
    {
      key: 'name',
      label: 'Name',
      render: (row) => `${row.firstName} ${row.lastName}`,
    },
    { key: 'email', label: 'Email' },
    {
      key: 'phoneNumber',
      label: 'Phone',
      render: (row) => row.phoneNumber || '—',
    },
  ];

  return (
    <AdminPageShell
      title="Customers"
      onBack={() => history.push('/admin/dashboard')}
    >
      <DataTable
        columns={columns}
        rows={customers}
        keyOf={(row) => row.id}
        loading={loading}
        mobileCard
        emptyMessage="No customers to display."
        loadingMessage="Loading customers…"
        aboveTable={
          <SearchBar
            setKeyword={(keyword) => {
              axiosKK
                .get<CustomerListResponse>(
                  keyword.length === 0
                    ? '/customers'
                    : `/customers/search/simple/${keyword}`,
                )
                .then(({ data }) => {
                  setCustomers(data.customers);
                });
            }}
          />
        }
      />

      {!hideButton && (
        <div className="admin-shell__more-row">
          <SubmitButton type="button" onClick={() => setPage(page + 1)}>
            Show more
          </SubmitButton>
        </div>
      )}
    </AdminPageShell>
  );
}

interface SearchBarProps {
  setKeyword: (keyword: string) => void;
}

function SearchBar({ setKeyword }: SearchBarProps): JSX.Element {
  const [search, setSearch] = useState('');
  const [dirty, setDirty] = useState(false);
  const [interval, setInterval] = useState<NodeJS.Timeout>();

  useEffect(() => {
    return () => {
      interval && clearTimeout(interval);
    };
  }, [interval]);

  return (
    <input
      onBlur={() => {
        setKeyword(search);
      }}
      value={search}
      className={'search-bar'}
      placeholder={'Search User'}
      onChange={(e) => {
        setDirty(true);
        setSearch(e.target.value);

        if (interval) {
          clearTimeout(interval);
        }
        setInterval(
          setTimeout(() => {
            if (dirty) {
              setDirty(false);
              setKeyword(search);
            }
          }, 250),
        );
      }}
    />
  );
}

export default withRouter(AdminCustomersListComponent);
